import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { LoaderCircle, type LucideProps } from 'lucide-react';
import { cn } from '@/lib/cn';

type SystemLoadingSize = 'sm' | 'md' | 'lg';
type SystemLoadingFill = 'parent' | 'screen' | 'content';
const sizeClass = { sm: 'h-6 w-6', md: 'h-10 w-10', lg: 'h-12 w-12' };
const fillClass = { parent: 'h-full min-h-[240px]', screen: 'min-h-screen', content: 'py-12' };

/** Gradient artwork for media placeholders and skeletons; compact controls use a spinner. */
export function SystemLoadingArtwork() {
  return <span className="system-loading-artwork" aria-hidden="true">
    <span className="system-loading-color" />
    <span className="system-loading-light" />
    <span className="system-loading-sweep" />
  </span>;
}

export function SystemLoadingSurface({ as: Tag = 'div', className, children, ...props }: HTMLAttributes<HTMLDivElement> & { as?: 'div' | 'span' }) {
  return <Tag {...props} className={cn('system-loading-surface', className)}>
    <SystemLoadingArtwork />
    {children}
  </Tag>;
}

export function SystemLoadingSpinner({ size = 'md', className }: { size?: SystemLoadingSize; className?: string }) {
  return <SystemLoadingIcon className={cn(sizeClass[size], className)} />;
}

/** Lucide-compatible adapter retaining icon-slot sizing, refs and accessibility. */
export const SystemLoadingIcon = forwardRef<SVGSVGElement, LucideProps>(function SystemLoadingIcon({
  size = 24, className, children: _children, ...props
}, ref) {
  const classes = className?.split(/\s+/).filter(value => !/(^|:)(animate-(spin|pulse|bounce)|border(?:-.+)?|rounded-full)$/.test(value)).join(' ');
  return <LoaderCircle ref={ref} size={size} aria-hidden={props['aria-label'] || props['aria-labelledby'] ? undefined : true} {...props}
    className={cn('system-loading-icon shrink-0 motion-safe:animate-spin motion-reduce:animate-none', classes)} />;
});

export function SystemLoading({ label, size = 'md', fill = 'parent', className, contentClassName }: {
  label?: ReactNode; size?: SystemLoadingSize; fill?: SystemLoadingFill; className?: string; contentClassName?: string;
}) {
  return <div role="status" aria-live="polite" aria-busy="true"
    className={cn('flex w-full items-center justify-center', fillClass[fill], className)}>
    <div className={cn('flex min-w-0 flex-col items-center gap-4 px-6 text-center', contentClassName)}>
      <SystemLoadingSurface className={cn('w-48 max-w-full rounded-xl', size === 'lg' ? 'h-36' : size === 'sm' ? 'h-20' : 'h-28')} />
      {label ? <span className="text-sm font-medium leading-5 text-slate-600">{label}</span> : null}
    </div>
  </div>;
}

/** Refresh/retry icons keep their idle meaning; only the busy branch becomes a spinner. */
export function SystemLoadingSwitch({ icon: Icon, className, ...props }: LucideProps & { icon: React.ComponentType<LucideProps> }) {
  return className?.split(/\s+/).some(value => /(^|:)animate-(spin|pulse)$/.test(value))
    ? <SystemLoadingIcon {...props} className={className} />
    : <Icon {...props} className={className} />;
}
